import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PrimaryButton } from '../../../../components/ui';

interface IssueWarningDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => Promise<void>;
    userName: string;
}

export const IssueWarningDialog: React.FC<IssueWarningDialogProps> = ({
    open,
    onClose,
    onConfirm,
    userName,
}) => {
    const theme = useTheme();
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirm = async () => {
        if (!reason.trim()) return;

        try {
            setIsSubmitting(true);
            await onConfirm(reason);
            setReason('');
            onClose();
        } catch (error) {
            // Error is handled by the parent
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 600 }}>Issue Warning</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        You are about to issue a warning to <strong>{userName}</strong>. This action will be recorded in the user's warning history.
                    </Typography>
                </Box>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Reason for Warning"
                    type="text"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={4}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={isSubmitting}
                    placeholder="Enter the reason for this warning..."
                    required
                />
            </DialogContent>
            <DialogActions sx={{ padding: theme.spacing(2, 3) }}>
                <Button onClick={onClose} disabled={isSubmitting} sx={{ color: theme.palette.text.secondary }}>
                    Cancel
                </Button>
                <PrimaryButton
                    onClick={handleConfirm}
                    disabled={isSubmitting || !reason.trim()}
                    sx={{
                        backgroundColor: theme.palette.warning.main,
                        color: theme.palette.common.black,
                        '&:hover': {
                            backgroundColor: theme.palette.warning.dark,
                        },
                        '&.Mui-disabled': {
                            backgroundColor: theme.palette.action.disabledBackground,
                            color: theme.palette.action.disabled,
                        }
                    }}
                >
                    {isSubmitting ? 'Issuing...' : 'Issue Warning'}
                </PrimaryButton>
            </DialogActions>
        </Dialog>
    );
};
